import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { styled } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";

import api from "../../service/api";
import { setError } from "../../reducers/appSlice";
import { selectAcciones } from "../../reducers/authSlice";
import BuscadorTema from "../../commons/BuscadorTema";

const Row = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const DatosTramite = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const acciones = useSelector(selectAcciones);
  const [buscandoTema, setBuscandoTema] = useState(false);
  const [soloTramites, setSoloTramites] = useState(false);

  const leyendaTema =
    "Podés buscar directamente por código o acceder al buscador ampliado en la lupa.";

  const validationSchema = Yup.object().shape({
    tipoTramite: Yup.string().required("Requerido"),
    codigoTema: Yup.number().required("Requerido"),
    tema: Yup.string().required("Requerido"),
    asunto: Yup.string().required("Requerido").max(1000, "Máximo 1000 caracteres"),
    observaciones: Yup.string().max(1000, "Máximo 1000 caracteres"),
    folios: Yup.number().when("soporte", {
      is: (soporte) => soporte === "papel",
      then: Yup.number()
        .positive("Debe ser mayor que cero")
        .required("Requerido"),
    }),
  });

  const formik = useFormik({
    initialValues: props.datosTramite,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      props.handleDatosTramite(values);
    },
    onReset: () => {},
  });

  const handleTipoTramite = (event) => {
    if (event.target.value === "expediente") {
      formik.setFieldValue("visibilidad", "1");
    }

    formik.handleChange(event);
  };

  const handleTema = (tema = null) => {
    if (tema !== null) {
      let soporte = tema.digital === "EN PAPEL" ? "papel" : "digital";
      formik.setFieldValue("codigoTema", tema.id);
      formik.setFieldValue("tema", tema.descTemaCompleto);
      formik.setFieldValue("soporte", soporte);
      if (soporte === "papel") {
        formik.setFieldValue("folios", 1);
      } else {
        formik.setFieldValue("folios", 0);
      }
    } else {
      formik.setFieldValue("tema", "");
      formik.setFieldValue("soporte", "");
      formik.setFieldValue("folios", 0);
    }
  };

  const handleCodigo = (event) => {
    if (event.keyCode === 13 || event.keyCode === 9) {
      if (event.target.value !== "" && event.target.value !== null) {
        buscarTemaPorCodigo(event.target.value);
      }
    } else if (
      event.type === "blur" &&
      formik.values.tema === "" &&
      event.target.value !== "" &&
      event.target.value !== null
    ) {
      buscarTemaPorCodigo(event.target.value);
    }
  };

  const handleChangeCodigo = (event) => {
    if (formik.values.tema !== "" && formik.values.tema !== null) handleTema();

    if (/^\d*$/.test(event.target.value)) formik.setFieldValue("codigoTema", event.target.value);
  };

  const handleChangeFolios = (event) => {    
    if (/^\d*$/.test(event.target.value)){
      formik.setFieldValue(event.target.id, event.target.value);
    }
  };

  const buscarTemaPorCodigo = (codigo) => {
    setBuscandoTema(true);
    api
      .buscarTema({
        id: codigo,
        descripcion: "",
        general: "T",
        digital: "T",
        activo: "S",
        alta: "S",
      })
      .then((res) => {
        if (res.data && res.data.length > 0) {
          handleTema(res.data[0]);
        } else {
          handleTema();
          dispatch(setError("No se encontró tema"));
        }
      })
      .catch((e) => {
        handleTema();
        dispatch(setError("Error al buscar el tema"));
      })
      .finally(() => setBuscandoTema(false));
  };

  useImperativeHandle(ref, () => ({
    validarDatos() {
      formik.submitForm();
    },
  }));

  useEffect(() => {
    for (let index = 0; index < acciones.length; index++) {
      if (
        acciones[index].funcion === props.funcion &&
        acciones[index].accion === "soloGeneraTramitesInternos"
      ) {
        setSoloTramites(true);
        formik.setFieldValue("tipoTramite", "tramite");
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div width="100%">
      <form
        id="buscador"
        name="buscador"
        onSubmit={formik.handleSubmit}
        onReset={formik.handleReset}
        noValidate
        autoComplete="off"
      >
        <Row container spacing={2}>
          <Grid item lg={2} xs={12}>
            <FormControl
              variant="outlined"
              size="small"
              fullWidth
              error={
                formik.touched.tipoTramite && Boolean(formik.errors.tipoTramite)
              }
            >
              <InputLabel id="tipoTramiteLabel">Tipo de trámite</InputLabel>
              <Select
                labelId="tipoTramiteLabel"
                id="tipoTramite"
                name="tipoTramite"
                variant="outlined"
                label="Tipo de trámite"
                value={formik.values.tipoTramite}
                onChange={handleTipoTramite}
                //disabled={soloTramites}
                disabled
              >
                <MenuItem value="expediente">Expediente</MenuItem>
                <MenuItem value="tramite">Trámite interno</MenuItem>
              </Select>
              <FormHelperText>
                {formik.touched.tipoTramite && formik.errors.tipoTramite}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item lg={2} xs={12}>
            <FormControl
              variant="outlined"
              size="small"
              fullWidth
              error={
                formik.touched.visibilidad && Boolean(formik.errors.visibilidad)
              }
              disabled={formik.values.tipoTramite === "expediente"}
            >
              <InputLabel id="visibilidadLabel">Visibilidad</InputLabel>
              <Select
                labelId="visibilidadLabel"
                label="Visibilidad"
                id="visibilidad"
                name="visibilidad"
                variant="outlined"
                fullWidth
                value={formik.values.visibilidad}
                onChange={formik.handleChange}
              >
                <MenuItem value="1">TOTAL</MenuItem>
                <MenuItem value="3">INTERNA</MenuItem>
              </Select>
              <FormHelperText>
                {formik.touched.visibilidad && formik.errors.visibilidad}
              </FormHelperText>
            </FormControl>
          </Grid>
        </Row>

        <Row container spacing={2}>
          <Grid item lg={2} xs={12}>
            <TextField
              id="codigoTema"
              name="codigoTema"
              label="Código de tema"
              variant="outlined"
              size="small"
              fullWidth
              inputProps={{ maxLength: 8 }}
              value={formik.values.codigoTema}
              onChange={handleChangeCodigo}
              onKeyDown={handleCodigo}
              onBlur={handleCodigo}
              error={
                formik.touched.codigoTema && Boolean(formik.errors.codigoTema)
              }
              helperText={formik.touched.codigoTema && formik.errors.codigoTema}
            />
            <Typography
              variant="aclaracion"
              sx={{
                display: {
                  xs: "inline",
                  sm: "inline",
                  md: "inline",
                  lg: "none",
                },
              }}
            >
              {leyendaTema}
            </Typography>
          </Grid>
          <Grid item lg={10} xs={12}>
            <Box display="flex" flexDirection="row">
              <TextField
                id="tema"
                name="tema"
                label="Tema"
                variant="outlined"
                size="small"
                fullWidth
                value={formik.values.tema}
                onChange={formik.handleChange}
                error={formik.touched.tema && Boolean(formik.errors.tema)}
                helperText={formik.touched.tema && formik.errors.tema}
                disabled
              />
              {buscandoTema ? (
                <CircularProgress
                  color="primary"
                  size={20}
                  sx={{ margin: 1 }}
                />
              ) : (
                <BuscadorTema resultado={handleTema} activo={"S"} alta={"S"} />
              )}
            </Box>
          </Grid>
          <Grid container>
            <Typography
              variant="aclaracion"
              sx={{
                ml: 2,
                display: { xs: "none", sm: "none", md: "none", lg: "inline" },
              }}
            >
              {leyendaTema}
            </Typography>
          </Grid>
        </Row>

        <Row container spacing={2}>
          <Grid item lg={2} xs={12}>
            <FormControl
              variant="outlined"
              size="small"
              fullWidth
              error={formik.touched.soporte && Boolean(formik.errors.soporte)}
              disabled
            >
              <InputLabel id="soporteLabel">Soporte</InputLabel>
              <Select
                labelId="soporteLabel"
                label="Soporte"
                id="soporte"
                name="soporte"
                variant="outlined"
                value={formik.values.soporte}
                fullWidth
                onChange={formik.handleChange}
              >
                <MenuItem value="digital">DIGITAL</MenuItem>
                <MenuItem value="papel">PAPEL</MenuItem>
              </Select>
              <FormHelperText>
                {formik.touched.soporte && formik.errors.soporte}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item lg={1} xs={12}>
            <TextField
              type="text"
              id="folios"
              name="folios"
              label="Folios"
              variant="outlined"
              size="small"
              fullWidth
              inputProps={{ maxLength: 5 }}
              value={formik.values.folios}
              onChange={handleChangeFolios}
              disabled={formik.values.soporte !== "papel"}
              error={formik.touched.folios && Boolean(formik.errors.folios)}
              helperText={formik.touched.folios && formik.errors.folios}
            />
          </Grid>
        </Row>

        <Row container>
          <Grid item lg={12} xs={12}>
            <TextField
              id="asunto"
              name="asunto"
              label="Asunto"
              variant="outlined"
              size="small"
              multiline
              rows={3}
              fullWidth
              inputProps={{ maxLength: 1000 }}
              value={formik.values.asunto}
              onChange={formik.handleChange}
              error={formik.touched.asunto && Boolean(formik.errors.asunto)}
              helperText={(formik.touched.asunto && formik.errors.asunto ) || "Máximo 1.000 caracteres"}
            />
          </Grid>
        </Row>

        <Row container>
          <Grid item lg={12} xs={12}>
            <TextField
              id="observaciones"
              name="observaciones"
              label="Observaciones"
              variant="outlined"
              size="small"
              multiline
              rows={3}
              fullWidth
              inputProps={{ maxLength: 1000 }}
              value={formik.values.observaciones}
              onChange={formik.handleChange}
              error={
                formik.touched.observaciones &&
                Boolean(formik.errors.observaciones)
              }
              helperText={
                (formik.touched.observaciones && formik.errors.observaciones) || "Máximo 1.000 caracteres"
              }
            />
          </Grid>
        </Row>
      </form>
    </div>
  );
});

DatosTramite.propTypes = {
  handleDatosTramite: PropTypes.func.isRequired,
  datosTramite: PropTypes.object.isRequired,
  funcion: PropTypes.string.isRequired,
};

DatosTramite.defaultProps = {
  datosTramite: {
    tipoTramite: "tramite",
    codigoTema: "",
    tema: "",
    soporte: "",
    visibilidad: 1,
    asunto: "",
    observaciones: "",
    folios: 0,
  },
};

export default DatosTramite;
